import { memo, useMemo, useCallback } from "react";
import { cn } from "@repo/design-system/utils";
import { ContinueShoppingLink } from "@/components/cart/ContinueShoppingLink";
import { collapseProvenance } from "@/utils/cart";
import { SummaryHeader } from "./SummaryHeader";
import { SellerSection } from "./SellerSection";
import { TotalsBreakdown } from "./TotalsBreakdown";
import { TrustBadges } from "./TrustBadges";
import { CheckoutButton } from "./CheckoutButton";
import type {
  OrderSummaryMode,
  StoreSellerCart,
  StoreSellerCartsSummary
} from "./types";

export interface OrderSummaryProps {
  /** Mode determines which features are shown */
  mode: OrderSummaryMode;
  /** Carts being displayed */
  carts: StoreSellerCart[];
  /** Aggregated summary across all carts */
  summary: StoreSellerCartsSummary;
  /** Selected cart IDs (cart mode: for "X sellers selected" text) */
  selectedCartIds?: string[];
  /** Callback when "Checkout All" is clicked (cart mode only) */
  onCheckout?: () => void;
  /**
   * Cart mode only: whether the Continue Shopping link shows, independent of
   * `onCheckout` (the combined checkout can be unavailable while the buyer
   * still needs a way out). Defaults to true.
   */
  showContinueShopping?: boolean;
  /** Whether checkout is disabled (cart mode only) */
  isCheckoutDisabled?: boolean;
  /** Whether checkout is in progress */
  isCheckoutLoading?: boolean;
  /** Callback when promo code is applied to a cart */
  onApplyPromo?: (cartId: string, code: string) => Promise<void>;
  /** Callback when promo code is removed from a cart */
  onRemovePromo?: (cartId: string) => Promise<void>;
  /** Map of cart ID to applied promo code */
  appliedPromoCodes?: Record<string, string>;
  /** Map of cart ID to promo code error */
  promoErrors?: Record<string, string>;
  /** Whether the summary is in a loading state */
  isLoading?: boolean;
  /** Checkout mode: receives the element the step's primary action portals into */
  actionSlotRef?: (element: HTMLDivElement | null) => void;
  /** Optional className for styling */
  className?: string;
}

/**
 * Reusable order summary component for both Cart and Checkout pages.
 *
 * Features by mode:
 * - Cart mode: Shows checkout button, "X sellers selected" text
 * - Checkout mode: Hides checkout button, compact display
 *
 * Both modes support:
 * - Quantity editing via line items
 * - Shipping method selection per seller
 * - Per-seller promo code input
 * - Totals breakdown
 * - Trust badges
 *
 * @example
 * ```tsx
 * // In Cart page
 * <OrderSummary
 *   mode="cart"
 *   carts={selectedCarts}
 *   summary={filteredSummary}
 *   selectedCartIds={selectedIds}
 *   onCheckout={handleCheckout}
 *   isCheckoutDisabled={selectedIds.length === 0}
 * />
 *
 * // In Checkout page
 * <OrderSummary
 *   mode="checkout"
 *   carts={checkoutCarts}
 *   summary={checkoutSummary}
 * />
 * ```
 */
export const OrderSummary = memo(function OrderSummary({
  mode,
  carts,
  summary,
  selectedCartIds = [],
  onCheckout,
  showContinueShopping = true,
  isCheckoutDisabled = false,
  isCheckoutLoading = false,
  onApplyPromo,
  onRemovePromo,
  appliedPromoCodes = {},
  promoErrors = {},
  isLoading = false,
  actionSlotRef,
  className
}: OrderSummaryProps) {
  // Alphabetical, so the summary lists suppliers in a stable, predictable
  // order rather than `carts`' original (usually fetch/cart-creation) order.
  const sortedCarts = useMemo(
    () => [...carts].sort((a, b) => a.seller.name.localeCompare(b.seller.name)),
    [carts]
  );

  // Check if all carts have a shipping method selected
  const allShippingMethodsSelected = useMemo(() => {
    if (carts.length === 0) return false;
    return carts.every((cart) => cart.shipping_methods?.length > 0);
  }, [carts]);

  // Provenance for the "Shipping Total" row, which rolls several sellers'
  // rates into one figure. The cross-seller rule applies here (an unmarked
  // seller sits the vote out), the same one the checkout's own freight
  // roll-up uses - `summary` carries no shipping-specific marker, so this has
  // to come off the carts.
  const shippingProvenance = useMemo(
    () =>
      collapseProvenance(
        carts.map((cart) => cart.shipping_methods?.[0]?.provenance)
      ),
    [carts]
  );

  // Handlers for promo codes with noop defaults
  const handleApplyPromo = useCallback(
    async (cartId: string, code: string) => {
      if (onApplyPromo) {
        await onApplyPromo(cartId, code);
      }
    },
    [onApplyPromo]
  );

  const handleRemovePromo = useCallback(
    async (cartId: string) => {
      if (onRemovePromo) {
        await onRemovePromo(cartId);
      }
    },
    [onRemovePromo]
  );

  const sellerCount = mode === "cart" ? selectedCartIds.length : carts.length;

  // Dims the summary's own content while it loads. Applied per section rather
  // than on the root so the checkout action slot is never inside it: the
  // portalled step button owns its disabled state, and an inherited
  // `pointer-events-none` would make Pay silently unclickable.
  const loadingDim = isLoading ? "pointer-events-none opacity-50" : undefined;

  return (
    <div
      className={cn(
        // `overflow-clip`, not `-hidden`: it rounds the corners the same way
        // without making this a scroll container, which would trap the
        // checkout action slot's `sticky` inside the card.
        "flex flex-col overflow-clip rounded-xl border border-gray-200 bg-white",
        className
      )}
      role="region"
      aria-label="Order summary"
      aria-busy={isLoading}
    >
      <div className={cn("flex flex-1 flex-col", loadingDim)}>
        {/* Header */}
        <SummaryHeader
          itemCount={summary.total_items}
          sellerCount={sellerCount}
          mode={mode}
        />

        {/* Content - a per-seller money roll-up: one row per supplier with its
          subtotal, scrollable. No item re-list (the left column owns the
          items, KAN-70) and, in cart mode, no meter or promo (those live on
          each seller card's footer, KAN-75). Checkout has no seller cards, so
          it keeps its per-seller promo here. */}
        <div className="max-h-[400px] flex-1 overflow-y-auto px-6 py-1">
          {/* A plain divided list on the card's own white, typeset like the
            totals rows below it - so the summary reads as one statement
            rather than a grey well of separately boxed cards. */}
          <div className="divide-y divide-gray-100">
            {sortedCarts.map((cart) => (
              <SellerSection
                key={cart.cart_id}
                cart={cart}
                disabled={isLoading}
                onApplyPromo={onApplyPromo ? handleApplyPromo : undefined}
                onRemovePromo={onRemovePromo ? handleRemovePromo : undefined}
                appliedPromoCode={appliedPromoCodes[cart.cart_id]}
                promoError={promoErrors[cart.cart_id]}
                showPromoCode={mode === "checkout" && !!onApplyPromo}
              />
            ))}
          </div>
        </div>

        {/* Totals Breakdown */}
        {/* Inset to the content edge, matching the divider above the Total box,
          rather than a full-bleed rule cutting across the card. */}
        <div className="mx-6 border-t border-gray-200" aria-hidden="true" />
        <div>
          <TotalsBreakdown
            shippingProvenance={shippingProvenance}
            summary={summary}
            allShippingMethodsSelected={allShippingMethodsSelected}
          />
        </div>

        {/* Checkout Button (cart mode only, and only when `onCheckout` is
          given - it is omitted when the combined checkout is unavailable) */}
        {mode === "cart" && onCheckout && (
          <CheckoutButton
            onClick={onCheckout}
            disabled={isCheckoutDisabled}
            isLoading={isCheckoutLoading}
            className="pb-3"
          />
        )}
        {/* Sits at the bottom of the summary, under the checkout button when
            it shows and on its own when it is hidden. */}
        {mode === "cart" && showContinueShopping && (
          <div className="flex justify-center px-6 pb-4">
            <ContinueShoppingLink />
          </div>
        )}
      </div>

      {/* Checkout mode: the step's primary action is portalled in here (see
          CheckoutActionSlot), so it sits under the total as on the cart. Kept
          outside the loading dim above. Sticky to the bottom of the page's
          height-capped summary column, so on a short screen with many
          suppliers the action stays in view while the rest scrolls. The 12px
          white fade above it softens the edge content scrolls under; at rest
          it lies over the totals' own 16px bottom padding, so it is unseen. */}
      {mode === "checkout" && actionSlotRef && (
        <div
          ref={actionSlotRef}
          className="empty:hidden lg:sticky lg:bottom-0 lg:z-10 lg:bg-white lg:before:pointer-events-none lg:before:absolute lg:before:inset-x-0 lg:before:-top-3 lg:before:h-3 lg:before:bg-gradient-to-t lg:before:from-white lg:before:content-['']"
        />
      )}

      {/* Trust Badges */}
      <TrustBadges className={loadingDim} />
    </div>
  );
});

// Re-export types and sub-components for flexibility
export type {
  OrderSummaryMode,
  StoreSellerCart,
  StoreSellerCartsSummary
} from "./types";
export { SummaryHeader } from "./SummaryHeader";
export { SellerSection } from "./SellerSection";
export { SellerPromoCode } from "./SellerPromoCode";
export { TotalsBreakdown } from "./TotalsBreakdown";
export { TrustBadges } from "./TrustBadges";
export { CheckoutButton } from "./CheckoutButton";
